

import React, { useState, useEffect, useRef } from "react";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import BASE_URL from "./config";
import { useOutletContext } from "react-router-dom";

export default function StockCount({
    user,
    showPrevDate,
    showPrevDateUser,
    setShowPrevDate,
    setShowPrevDateUser,
}) {
    const [stockData, setStockData] = useState([]);
    const [warehouses, setWarehouses] = useState([]);
    const [selectedWarehouse, setSelectedWarehouse] = useState("");
    const [editingIndex, setEditingIndex] = useState(null);
    const [editedValue, setEditedValue] = useState("");
    const inputRef = useRef(null);
    const [today, setToday] = useState("");
    const { selectedCompany } = useOutletContext();
    // const [showPrevDate, setShowPrevDate] = useState(false);
    // const [showPrevDateUser, setShowPrevDateUser] = useState(false);// Ensure this is passed as a prop or managed via context
    const normalizedRole = user?.role?.toLowerCase();

    const shouldShowPrevStock =
        (normalizedRole === "superadmin" && showPrevDate) ||
        (normalizedRole === "user" && showPrevDateUser) || (normalizedRole === "admin" && showPrevDate) ;

    const updateSetting = async (name, value) => {
        const token = localStorage.getItem("authToken");
        try {
            const res = await fetch(`${BASE_URL}/api/show-prev-date/`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `token ${token}`,
                },
                body: JSON.stringify({ name, show: value }),
            });

            if (!res.ok) {
                throw new Error("Failed to update setting");
            }

            // ✅ Re-fetch updated settings to reflect change
            const updatedSettings = await fetch(`${BASE_URL}/api/show-prev-date/`, {
                headers: {
                    Authorization: `token ${token}`,
                },
            });

            const settingsData = await updatedSettings.json();
            //   console.log("Updated settings after post:", settingsData); // Debug

            const userSetting = settingsData.find((s) => s.name === "show_prev_date_user");
            const adminSetting = settingsData.find((s) => s.name === "show_prev_date");

            // ✅ Force boolean parsing (some APIs return strings like "true"/"false")
            const toBool = (v) => v === true || v === "true";

            if (userSetting) {
                // console.log("Resolved userSetting.show to:", toBool(userSetting.show));
                setShowPrevDateUser(toBool(userSetting.show));
            }
            if (adminSetting) {
                // console.log("Resolved adminSetting.show to:", toBool(adminSetting.show));
                setShowPrevDate(toBool(adminSetting.show));
            }

        } catch (error) {
            //   console.error("Error updating setting:", error);
        }
    };


    const exportToExcel = (includeData = true) => {
        const headers = ["Product Name"];
        if (showPrevDate) headers.push("Previous Stock");
        headers.push("Current Stock");
    
        const data = stockData.map(item => {
            const row = [item.product_name];
    
            if (showPrevDate) {
                row.push(includeData ? (item.prev ?? "") : "");
            }
    
            row.push(includeData ? (item.current === "" || item.current === null ? "0" : item.current) : "");
    
            return row;
        });
    
        const worksheet = XLSX.utils.aoa_to_sheet([headers, ...data]);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, "Stock Data");
    
        const today = new Date().toISOString().split("T")[0];
        const filename = includeData
            ? `stock_data_${today}.xlsx`
            : `stock_product_list_${today}.xlsx`;
    
        const excelBuffer = XLSX.write(workbook, { bookType: "xlsx", type: "array" });
        const dataBlob = new Blob([excelBuffer], { type: "application/octet-stream" });
        saveAs(dataBlob, filename);
    };
    

    const exportToPDF = (includeData = true) => {
        const doc = new jsPDF();
    
        const today = new Date().toISOString().split("T")[0];
    
      // Add Heading
doc.setFontSize(18);
doc.setTextColor(0, 0, 0); // Black text
doc.text(`Stock Count on ${today}`, doc.internal.pageSize.getWidth() / 2, 13, { align: 'center' });

        const headers = ["Product Name"];
        if (includeData && showPrevDate) headers.push("Previous Stock");
        headers.push("Current Stock"); // Always add Current Stock, even if includeData is false
    
        const data = stockData.map(item => {
            const row = [item.product_name];
            if (includeData && showPrevDate) {
                row.push(item.prev ?? "-");
            } else if (showPrevDate) {
                row.push("-");
            }
            if (includeData) {
                row.push(item.current === "" || item.current === null ? "-" : item.current);
            } else {
                row.push(""); // Current stock empty if no data
            }
            return row;
        });
    
        autoTable(doc, {
            startY: 25, // Start after the header
            head: [headers],
            body: data,
            styles: {
                fillColor: [240, 240, 240], // Light grey background for cells
                textColor: [50, 50, 50],    // Dark text
                halign: 'center',
                valign: 'middle',
            },
            headStyles: {
                fillColor: [255, 255, 255],
                textColor: [0, 0, 0],
                fontSize: 9,
                fontStyle: 'bold',
                halign: 'center',
                lineColor: [200, 200, 200],
                lineWidth: 0.5,
              },
            alternateRowStyles: {
                fillColor: [220, 220, 220], // Slightly lighter grey for alternate rows
            },
            margin: { top: 20 },
            theme: "grid",
        });
    
        const filename = includeData
            ? `stock_data_${today}.pdf`
            : `stock_product_list_${today}.pdf`;
    
        doc.save(filename);
    };
    

    // ✅ Get today's date from backend (IST)
    useEffect(() => {
        fetch(`${BASE_URL}/api/today/`)
            .then(res => res.json())
            .then(data => {
                setToday(data.today); // Format: YYYY-MM-DD
            })
        // .catch(err => console.error("Failed to fetch today's date", err));
    }, []);

    // ✅ Load warehouses
useEffect(() => {
  const fetchWarehouses = async () => {
    try {
      const token = localStorage.getItem("authToken");
      if (!token) return;

      // Fetch current user info to get companies
      const userRes = await fetch(`${BASE_URL}/api/users/me/`, {
        headers: {
          Authorization: `Token ${token}`,
          "Content-Type": "application/json",
        },
      });
      if (!userRes.ok) throw new Error("Failed to fetch user info");
      const user = await userRes.json();

      // Determine companyId from selectedCompany or user companies
      let companyId = null;
      if (user.role === "superadmin") {
        companyId = selectedCompany?.id || user.companies?.[0];
      } else {
        companyId = user.companies?.[0];
      }

      if (!companyId) return;

      // Fetch warehouses for the company
      const res = await fetch(`${BASE_URL}/api/get_warehouse/?company_id=${companyId}`, {
        headers: {
          Authorization: `Token ${token}`,
          "Content-Type": "application/json",
        },
      });
      if (!res.ok) throw new Error("Failed to fetch warehouses");
      const data = await res.json();

      setWarehouses(data);
      if (data.length > 0) setSelectedWarehouse(data[0].id);
    } catch (err) {
      console.error(err);
    }
  };

  fetchWarehouses();
}, [selectedCompany]);


  useEffect(() => {
  if (!selectedWarehouse || !today) return;

  const fetchStock = async () => {
    try {
      const token = localStorage.getItem("authToken");
      if (!token) return;

      const res = await fetch(
        `${BASE_URL}/api/get_products_with_latest_stock/?warehouse_id=${selectedWarehouse}&date=${today}`,
        {
          headers: {
            Authorization: `Token ${token}`,
            "Content-Type": "application/json",
          },
        }
      );
      if (!res.ok) throw new Error("Failed to fetch stock");

      const data = await res.json();

      const merged = data.map((item) => ({
        ...item,
        current: item.current_quantity ?? "",
        prev: item.previous_quantity ?? 0,
        prev_date: item.previous_date ?? null,
      }));

      setStockData(merged);
    } catch (err) {
      console.error(err);
    }
  };

  fetchStock();
}, [selectedWarehouse, today]);



    
    const handleWarehouseChange = (e) => {
        setSelectedWarehouse(e.target.value);
    };

    const handleCellClick = (index) => {
        setEditingIndex(index);
        setEditedValue(stockData[index].current?.toString() ?? "");
        setTimeout(() => inputRef.current?.focus(), 0);
    };

    const handleBlur = () => {
        if (editingIndex === null) return;

        const updated = [...stockData];
        const currentItem = updated[editingIndex];
        const newValue = editedValue === "" || isNaN(editedValue) ? null : parseFloat(editedValue);
        currentItem.current = newValue !== null ? newValue : "";

        setStockData(updated);
        setEditingIndex(null);
        setEditedValue("");

        if (newValue !== null) {
            const payload = {
                product_id: currentItem.product_id,
                warehouse_id: selectedWarehouse,
                date: today, // use server-side date
                quantity: newValue,
            };

            // console.log("Payload being sent:", payload);
            // console.log("Date used in payload:", payload.date);

            fetch(`${BASE_URL}/api/update_stock/`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                },
                body: JSON.stringify(payload),
            })
                .then(async res => {
                    const data = await res.json();
                    if (!res.ok) {
                        throw new Error(data.detail || "Failed to update stock");
                    }
                    // console.log("Stock updated:", data);
                })
                .catch(err => {
                    // console.error("Failed to update stock:", err);
                });
        }
    };


    const handleKeyDown = (e) => {
        if (editingIndex === null) return;
        if (e.key === "ArrowDown" || e.key === "ArrowUp") {
            handleBlur();
            const newIndex = e.key === "ArrowDown" ? editingIndex + 1 : editingIndex - 1;
            if (newIndex >= 0 && newIndex < stockData.length) {
                handleCellClick(newIndex);
            }
        }
    };

    const handleInputChange = (e) => {
        let val = e.target.value.replace(/[^0-9.]/g, "");
        const dotCount = (val.match(/\./g) || []).length;
        if (dotCount > 1) {
            val = val.slice(0, val.lastIndexOf("."));
        }
        setEditedValue(val);
    };

    const handleKeepPreviousStock = () => {
        const updated = [...stockData];
        const todayDate = today; // from your state already set by the API

        updated.forEach((item, index) => {
            if (item.prev !== null && item.prev !== undefined) {
                item.current = item.prev;

                // Immediately save to backend
                const payload = {
                    product_id: item.product_id,
                    warehouse_id: selectedWarehouse,
                    date: todayDate,
                    quantity: item.prev,
                };

                // console.log("Saving copied stock to backend:", payload); // 👈 Debug print

                fetch(`${BASE_URL}/api/update_stock/`, {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                    },
                    body: JSON.stringify(payload),
                })
                    .then(res => res.json())
                    .then(data => {
                        // console.log("Saved:", data);
                    })
                    .catch(err => {
                        // console.error("Error updating stock:", err);
                    });
            }
        });

        setStockData(updated);
    };


    const formattedToday = today
        ? new Date(today).toLocaleDateString("en-GB")
        : "Loading...";

    return (
        <div className="min-h-screen bg-white dark:bg-gray-900 text-gray-900 dark:text-white p-4">
            <div className="w-full mx-auto space-y-4">
                <h1 className="text-2xl font-bold">Stock Count</h1>
                <div className="flex flex-wrap items-center justify-between gap-4">
                    <div className="text-sm font-medium">
                        Today's Date: <span className="font-normal">{formattedToday}</span>
                    </div>
                    <div className="flex items-center gap-2 mx-auto">
                        <label htmlFor="warehouse" className="text-sm font-medium">
                            Select Warehouse:
                        </label>
                        <select
                            id="warehouse"
                            value={selectedWarehouse}
                            onChange={handleWarehouseChange}
                            className="p-2 rounded border border-gray-300 dark:bg-gray-800 dark:border-gray-700 text-sm"
                        >
                            {warehouses.map((wh) => (
                                <option key={wh.id} value={wh.id}>
                                    {wh.name}
                                </option>
                            ))}
                        </select>
                    </div>
                    <button
                        className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded text-sm"
                        onClick={handleKeepPreviousStock}
                    >
                        Keep Previous Stock
                    </button>

                </div>
                {(user?.role === "superadmin" || user?.role === "Admin") && (
                    <div className="flex flex-wrap gap-4">
                        <label className="flex items-center gap-2 text-sm">
                            <input
                                type="checkbox"
                                className="accent-blue-600"
                                checked={showPrevDate}
                                onChange={() => {
                                    const newValue = !showPrevDate;
                                    setShowPrevDate(newValue);
                                    updateSetting("show_prev_date", newValue);
                                }}
                            />
                            Show Prev Date
                        </label>
                        <label className="flex items-center gap-2 text-sm">
                            <input
                                type="checkbox"
                                className="accent-blue-600"
                                checked={showPrevDateUser}
                                onChange={(e) => {
                                    const newValue = e.target.checked; // Proper boolean value
                                    setShowPrevDateUser(newValue);
                                    updateSetting("show_prev_date_user", newValue);
                                }}
                            />
                            Show Prev Date (User)
                        </label>


                    </div>
                )}

                <div className="flex flex-wrap gap-2">
                    <button className="bg-green-500 hover:bg-green-600 text-white px-4 py-2 rounded text-sm"
                        onClick={() => exportToExcel(true)}
                    >
                        Export to Excel
                    </button>
                    <button className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded text-sm"
                        onClick={() => exportToExcel(false)}
                    >
                        Export without Data (Excel)
                    </button>
                    <button className="bg-yellow-500 hover:bg-yellow-600 text-white px-4 py-2 rounded text-sm"
                        onClick={() => exportToPDF(false)}
                    >
                        Export without Data (PDF)
                    </button>
                    <button className="bg-red-500 hover:bg-red-600 text-white px-4 py-2 rounded text-sm"
                        onClick={() => exportToPDF(true)}
                    >
                        Export to PDF
                    </button>
                </div>

                <div className="overflow-x-auto border rounded-lg shadow dark:border-gray-700">
                    <table className="min-w-full text-sm text-left border-collapse">
                        <thead className="bg-gray-200 dark:bg-gray-800 text-gray-700 dark:text-gray-200">
                            <tr>
                                <th className="p-3 border-b dark:border-gray-700">Product Name</th>

                                {/* Conditionally show 'Stock on' column */}
                                {shouldShowPrevStock && (
                                    <th className="p-3 border-b dark:border-gray-700">
                                        Stock on {
                                            (() => {
                                                try {
                                                    const validPrev = stockData.find((item) => item.prev_date);
                                                    if (validPrev) {
                                                        return new Date(validPrev.prev_date).toLocaleDateString("en-GB");
                                                    } else {
                                                        const yesterday = new Date();
                                                        yesterday.setDate(yesterday.getDate() - 1);
                                                        return yesterday.toLocaleDateString("en-GB");
                                                    }
                                                } catch (err) {
                                                    // console.error("Error rendering prev stock date:", err);
                                                    return "Invalid Date"; // Fallback visible to user
                                                }
                                            })()
                                        }
                                    </th>
                                )}

                                <th className="p-3 border-b dark:border-gray-700">Current Stock</th>
                            </tr>
                        </thead>
                        <tbody>
                            {stockData.map((item, index) => (
                                <tr
                                    key={item.id}
                                    className={
                                        index % 2 === 0
                                            ? "bg-white dark:bg-gray-900"
                                            : "bg-gray-50 dark:bg-gray-800"
                                    }
                                >
                                    <td className="p-3 border-b dark:border-gray-700">
                                        {item.product_name}
                                    </td>

                                    {shouldShowPrevStock && (
                                        <td className="p-3 border-b dark:border-gray-700">
                                            {item.prev ?? "-"}
                                        </td>
                                    )}


                                    <td
                                        className="p-3 border-b dark:border-gray-700 cursor-pointer"
                                        onClick={() => handleCellClick(index)}
                                    >
                                        {editingIndex === index ? (
                                            <input
                                                ref={inputRef}
                                                type="text"
                                                value={editedValue}
                                                onChange={handleInputChange}
                                                onBlur={handleBlur}
                                                onKeyDown={handleKeyDown}
                                                className="w-full px-2 py-1 border rounded text-sm bg-white dark:bg-gray-700 dark:text-white outline-none"
                                            />
                                        ) : (
                                            <span>
                                                {item.current === "" || item.current === null ? "Add" : item.current}
                                            </span>
                                        )}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>

            </div>
        </div>
    );
};

